import type {
  ActivityEntry,
  ClientCapabilityProfile,
  ContinueWatchingEntry,
  DiscoveredRelease,
  DiscoveredTitle,
  AddedBatch,
  CatalogueGenre,
  CataloguePage,
  CatalogueRow,
  CatalogueSeasonDetails,
  CatalogueTitleDetails,
  CatalogueTitleRef,
  CatalogueVersions,
  DiscoveryKind,
  DiscoveryProvider,
  DiscoveryState,
  DiscoveryRepair,
  Library as LibraryModel,
  MetadataProvider,
  CurrentUser,
  ImageKind,
  ItemImage,
  PersonDetails,
  PlaybackOptions,
  PlaybackProgress,
  PlayOnDemandResult,
  UserPreferences,
  HardwareProbeResult,
  LibraryItem,
  LibraryItemFilter,
  LibraryItemPage,
  Library,
  LiveTvBouquet,
  LiveTvBouquetSummary,
  LiveTvChannel,
  LiveTvChannelOverride,
  LiveTvChannelRef,
  LiveTvLineupChannel,
  LiveTvProgram,
  LiveTvProviderSummary,
  LiveTvSourceSummary,
  LiveTvScanner,
  LiveTvScanStart,
  LiveTvScanStatus,
  LiveTvSearchResult,
  LiveTvSession,
  LiveTvSessionDetails,
  LiveTvTunerStatus,
  MediaSourceProvider,
  MediaSourceSummary,
  PlaybackSession,
  PluginConfigurationValues,
  PluginConfigurationView,
  PluginDescriptor,
  PendingPluginChange,
  RememberedTracks,
  RoleDescription,
  RecentItems,
  RuntimeEnvironment,
  ServerSettings,
  ServerSettingsUpdate,
  SetupState,
  SignInResponse,
  SystemInfo,
  TrackChoice,
  TranscodingSettingsView,
  UserRole,
} from './types';

/// Thrown for any non-2xx response. The server answers failures as RFC 9457 problem details
/// with a stable code and a remedy, and both are carried through to the UI.
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly remedy?: string,
    /// Per-field failures, sent by endpoints that validate a document rather than a value.
    readonly errors?: { path: string; code: string; message: string }[],
    /// The whole problem document. Its extra fields are the interpolation values for the
    /// translated message - the offending path, for instance.
    readonly data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const TOKEN_STORAGE_KEY = 'emiro.token';

/// The token lives in localStorage so a reload does not sign the user out. It is sent as a
/// bearer header everywhere, and as a query parameter only on media URLs, where a media
/// element cannot set headers.
export const tokenStorage = {
  read(): string | null {
    try {
      return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  write(token: string | null) {
    try {
      if (token === null) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      } else {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      }
    } catch {
      // A browser with site data blocked keeps the session for this tab only.
    }
  },
};

/// Called when the server rejects the stored token, so the app can return to the sign-in
/// screen instead of showing a wall of failed requests.
let onUnauthorized: (() => void) | undefined;

export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/// The error a failed answer stands for, read from the problem document the server sent with
/// it - or from the status alone, where it sent none.
function problemError(status: number, statusText: string, problem: ProblemDocument | null): ApiError {
  return new ApiError(
    problem?.detail ?? problem?.title ?? `${status} ${statusText}`,
    status,
    problem?.code,
    problem?.remedy,
    problem?.errors,
    problem ?? undefined,
  );
}

/// What the server says about a failed request, as far as anything here reads it.
interface ProblemDocument extends Record<string, unknown> {
  title?: string;
  detail?: string;
  code?: string;
  remedy?: string;
  errors?: { path: string; code: string; message: string }[];
}

/// The error behind a request that something other than this client made - the playlist and
/// segment requests of a media player, which hands over the request it failed on and nothing
/// more. Its answer is read the way `request` reads one; undefined where there is no answer to
/// read, or where it did not come from the server's own problem handling.
export function problemOfRequest(details: unknown): ApiError | undefined {
  if (typeof XMLHttpRequest === 'undefined' || !(details instanceof XMLHttpRequest) || details.status < 400) {
    return undefined;
  }

  try {
    // Asked for as text where the player reads a playlist, and as bytes where it reads a
    // segment - the problem document is the same JSON either way.
    const body =
      details.responseType === 'arraybuffer' && details.response instanceof ArrayBuffer
        ? new TextDecoder().decode(details.response)
        : details.responseType === '' || details.responseType === 'text'
          ? details.responseText
          : undefined;

    const problem = body ? (JSON.parse(body) as ProblemDocument) : null;

    return problem && typeof problem === 'object' && typeof problem.code === 'string'
      ? problemError(details.status, details.statusText, problem)
      : undefined;
  } catch {
    return undefined;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenStorage.read();

  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 401 && token) {
    tokenStorage.write(null);
    onUnauthorized?.();
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null);

    throw problemError(response.status, response.statusText, problem);
  }

  // No content: there is no body to read.
  if (response.status === 204) {
    return undefined as T;
  }

  // Accepted may or may not say something yet - a started scan answers with its first status,
  // other work that was merely queued with nothing at all.
  if (response.status === 202) {
    const text = await response.text();

    return (text ? JSON.parse(text) : undefined) as T;
  }

  return (await response.json()) as T;
}

/// The address of an item's artwork, or undefined when it has none. An image element cannot
/// send an Authorization header, so the token goes into the URL the way it does for media; the
/// tag makes the address change whenever the picture does, which is what lets it be cached.
/// Where one picture is served from. Which item it is stored under is the server's answer, not
/// this one's: an episode nobody drew a still for carries its season's picture, and a season
/// its series'. The tag is what lets the browser keep it until the picture itself changes.
export function imageUrl(image: ItemImage | null | undefined, kind: ImageKind = 'Primary'): string | undefined {
  if (!image) {
    return undefined;
  }

  const token = tokenStorage.read();

  return `/api/items/${image.itemId}/images/${kind}?tag=${image.tag}`
    + (token ? `&token=${encodeURIComponent(token)}` : '');
}

export function itemImageUrl(
  item: { primaryImage: ItemImage | null; backdropImage: ItemImage | null; logoImage: ItemImage | null },
  kind: ImageKind = 'Primary',
): string | undefined {
  return imageUrl({ Primary: item.primaryImage, Backdrop: item.backdropImage, Logo: item.logoImage }[kind], kind);
}

/// A channel's logo. Most point wherever the playlist said; a logo a tuner plugin carries itself
/// is served by this server in two styles, and the page picks the one drawn for its background.
export function channelLogoUrl(logo: string, background: 'dark' | 'light'): string {
  if (!logo.startsWith('/api/livetv/logo?')) {
    return logo;
  }

  const token = tokenStorage.read();

  return `${logo}&style=${background === 'dark' ? 'Dark' : 'Light'}`
    + (token ? `&token=${encodeURIComponent(token)}` : '');
}

/// A person's portrait, served by our own server rather than by the metadata provider, so a
/// cast list never makes the browser talk to anyone else.
export function personImageUrl(providerKey: string, personId: string, language: string): string {
  const token = tokenStorage.read();

  return `/api/people/${encodeURIComponent(providerKey)}/${encodeURIComponent(personId)}/image`
    + `?language=${encodeURIComponent(language)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
}

/// Where the server answers about one catalogue title.
function titleRoute(title: CatalogueTitleRef): string {
  return `/api/discovery/titles/${encodeURIComponent(title.provider)}/${title.kind}/${encodeURIComponent(title.id)}`;
}

/// The page of a catalogue title in this application.
export function catalogueTitlePath(title: CatalogueTitleRef): string {
  return `/titles/${encodeURIComponent(title.provider)}/${title.kind}/${encodeURIComponent(title.id)}`;
}

export const api = {
  setupState: () => request<SetupState>('/api/setup/state'),
  createFirstAdministrator: (body: {
    name: string;
    password: string;
    preferredLanguage: string;
    clientName: string;
  }) => request<SignInResponse>('/api/setup/administrator', { method: 'POST', body: JSON.stringify(body) }),

  signIn: (body: { userName: string; password: string; clientName: string }) =>
    request<SignInResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  signOut: () => request<void>('/api/auth/logout', { method: 'POST' }),
  currentUser: () => request<CurrentUser>('/api/auth/me'),

  users: () => request<CurrentUser[]>('/api/users'),
  myPreferences: () => request<UserPreferences>('/api/users/me/preferences'),
  saveMyPreferences: (preferences: UserPreferences) =>
    request<UserPreferences>('/api/users/me/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    }),
  roles: () => request<RoleDescription[]>('/api/users/roles'),
  createUser: (body: { name: string; password: string; role: UserRole }) =>
    request<CurrentUser>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  deleteUser: (userId: string) => request<void>(`/api/users/${userId}`, { method: 'DELETE' }),
  setUserRole: (userId: string, role: UserRole) =>
    request<CurrentUser>(`/api/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),
  /// An administrator setting somebody else's password. It needs no proof of who is typing,
  /// which is exactly why it needs the permission instead.
  setUserPassword: (userId: string, password: string) =>
    request<void>(`/api/users/${userId}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    }),
  /// Changing one's own, which needs the password in use rather than a permission.
  changeMyPassword: (currentPassword: string, password: string) =>
    request<void>('/api/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, password }),
    }),

  createLibrary: (body: {
    id: string;
    name: string;
    kind: string;
    /// BCP 47 tag the metadata providers are asked in.
    metadataLanguage?: string;
    locations: { id: string; providerId: string; address: string }[];
  }) => request<Library>('/api/libraries', { method: 'POST', body: JSON.stringify(body) }),

  systemInfo: () => request<SystemInfo>('/api/system/info'),
  runtime: () => request<RuntimeEnvironment>('/api/system/runtime'),

  settings: () => request<ServerSettings>('/api/settings'),
  saveSettings: (update: ServerSettingsUpdate) =>
    request<ServerSettings>('/api/settings', { method: 'PUT', body: JSON.stringify(update) }),
  transcodingSettings: () => request<TranscodingSettingsView>('/api/settings/transcoding'),
  hardware: () => request<HardwareProbeResult>('/api/system/hardware'),
  refreshHardware: () => request<HardwareProbeResult>('/api/system/hardware/refresh', { method: 'POST' }),
  plugins: () => request<PluginDescriptor[]>('/api/plugins'),
  /// Uploads a plugin - a zip of its folder, or its .dll - to be installed, or to update the
  /// plugin with the id in `replaces`, when the plugins are next reloaded.
  uploadPlugin: (file: File, replaces?: string) => {
    const query = new URLSearchParams({ fileName: file.name });

    if (replaces) {
      query.set('replaces', replaces);
    }

    return request<PendingPluginChange>(`/api/plugins/packages?${query.toString()}`, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  },
  pendingPluginChanges: () => request<PendingPluginChange[]>('/api/plugins/packages'),
  discardPluginChange: (folder: string) =>
    request<void>(`/api/plugins/packages/${encodeURIComponent(folder)}`, { method: 'DELETE' }),
  removePlugin: (pluginId: string) =>
    request<PendingPluginChange>(`/api/plugins/${encodeURIComponent(pluginId)}`, { method: 'DELETE' }),
  /// Starts the server over in place, so waiting plugin changes take effect.
  reloadPlugins: () => request<void>('/api/plugins/reload', { method: 'POST' }),
  pluginTranslations: (language: string) =>
    request<Record<string, string>>(`/api/plugins/i18n/${encodeURIComponent(language)}`),
  pluginConfiguration: (pluginId: string) =>
    request<PluginConfigurationView>(`/api/plugins/${encodeURIComponent(pluginId)}/configuration`),
  savePluginConfiguration: (pluginId: string, values: PluginConfigurationValues) =>
    request<void>(`/api/plugins/${encodeURIComponent(pluginId)}/configuration`, {
      method: 'PUT',
      body: JSON.stringify(values),
    }),

  items: (filter: LibraryItemFilter = {}) => {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== '') {
        query.set(key, String(value));
      }
    }

    return request<LibraryItemPage>(`/api/items?${query.toString()}`);
  },
  item: (itemId: string) => request<LibraryItem>(`/api/items/${itemId}`),
  /// Every file one entry stands for. Usually one; several where the same film is on the
  /// server more than once.
  itemVersions: (itemId: string) => request<MediaSourceSummary[]>(`/api/items/${itemId}/versions`),
  person: (providerKey: string, personId: string, language: string) =>
    request<PersonDetails>(
      `/api/people/${encodeURIComponent(providerKey)}/${encodeURIComponent(personId)}`
      + `?language=${encodeURIComponent(language)}`,
    ),
  refreshItemMetadata: (itemId: string) =>
    request<LibraryItem>(`/api/items/${itemId}/metadata/refresh`, { method: 'POST' }),
  itemChildren: (itemId: string) => request<LibraryItemPage>(`/api/items/${itemId}/children`),
  recentItems: (take = 12) => request<RecentItems>(`/api/items/recent?take=${take}`),

  continueWatching: (take = 12) =>
    request<ContinueWatchingEntry[]>(`/api/items/continue?take=${take}`),
  /// Where the signed-in user is in everything they have started, keyed by item id.
  playbackProgress: () => request<Record<string, PlaybackProgress>>('/api/items/progress'),
  reportProgress: (itemId: string, positionSeconds: number, durationSeconds?: number) =>
    request<PlaybackProgress>(`/api/items/${itemId}/progress`, {
      method: 'POST',
      body: JSON.stringify({ positionSeconds, durationSeconds }),
    }),
  clearProgress: (itemId: string) =>
    request<void>(`/api/items/${itemId}/progress`, { method: 'DELETE' }),

  /// What this person last watched this title with, answered through the title the choice is
  /// kept on: a film itself, a programme for any of its episodes.
  itemTracks: (itemId: string) => request<RememberedTracks>(`/api/items/${itemId}/tracks`),
  /// Remembers the soundtrack and the subtitle somebody picked, by language. Nothing chosen at
  /// all - no language either way and subtitles not switched off - forgets the title again.
  saveItemTracks: (itemId: string, tracks: TrackChoice) =>
    request<RememberedTracks>(`/api/items/${itemId}/tracks`, {
      method: 'PUT',
      body: JSON.stringify(tracks),
    }),

  libraries: () => request<Library[]>('/api/libraries'),
  library: (libraryId: string) => request<Library>(`/api/libraries/${libraryId}`),
  saveLibrary: (library: LibraryModel) =>
    request<Library>('/api/libraries', { method: 'POST', body: JSON.stringify(library) }),
  deleteLibrary: (libraryId: string) => request<void>(`/api/libraries/${libraryId}`, { method: 'DELETE' }),
  metadataProviders: () => request<MetadataProvider[]>('/api/libraries/metadata/providers'),
  mediaSourceProviders: () => request<MediaSourceProvider[]>('/api/libraries/providers'),
  scanLibrary: (libraryId: string) =>
    request<void>(`/api/libraries/${libraryId}/scan`, { method: 'POST' }),
  refreshLibraryMetadata: (libraryId: string, force = false) =>
    request<void>(`/api/libraries/${libraryId}/metadata/refresh?force=${force}`, { method: 'POST' }),

  startPlayback: (mediaSourceId: string, client: unknown, options: PlaybackOptions = {}) =>
    request<{ session: PlaybackSession; streamUrl: string; startPositionSeconds: number }>(
      '/api/playback/sessions',
      { method: 'POST', body: JSON.stringify({ mediaSourceId, client, ...options }) },
    ),

  /// Moves a running transcode to another moment. The session, its decision and its URL stay;
  /// only the worker behind them starts again where somebody dragged the bar to.
  seekPlayback: (sessionId: string, positionTicks: number) =>
    request<{ session: PlaybackSession; streamUrl: string; startPositionSeconds: number }>(
      `/api/playback/sessions/${sessionId}/seek`,
      { method: 'POST', body: JSON.stringify({ positionTicks }) },
    ),

  sessions: () => request<PlaybackSession[]>('/api/playback/sessions'),

  /// Now playing: every film and channel session, joined with title, user, client and delivery.
  /// Everything with ViewSessions, one's own without.
  activity: () => request<ActivityEntry[]>('/api/activity'),

  /// "Still here." A paused player asks for no bytes, and without this the server cannot tell
  /// that apart from a window somebody closed.
  reportPresence: (sessionId: string) =>
    request<void>(`/api/playback/sessions/${sessionId}/presence`, { method: 'POST' }),

  /// `keepalive` lets the request outlive the page, which is the only way a tab that is being
  /// closed gets to say so.
  stopSession: (sessionId: string, options?: { keepalive?: boolean }) =>
    request<void>(`/api/playback/sessions/${sessionId}`, { method: 'DELETE', ...options }),

  /// Every channel nobody hid, or - given a bouquet - that bouquet's channels in its order.
  liveTvChannels: (bouquetId?: string) =>
    request<LiveTvChannel[]>(
      bouquetId ? `/api/livetv/channels?bouquetId=${encodeURIComponent(bouquetId)}` : '/api/livetv/channels',
    ),
  liveTvSearch: (query: string) =>
    request<LiveTvSearchResult>(`/api/livetv/search?query=${encodeURIComponent(query)}`),
  liveTvGuide: (from: Date, until: Date) =>
    request<LiveTvProgram[]>(
      `/api/livetv/guide?from=${encodeURIComponent(from.toISOString())}`
      + `&until=${encodeURIComponent(until.toISOString())}`,
    ),
  /// A channel is decided the same way a film is: what comes down it is only known once it
  /// has been probed, and what to do about it is this client's own capabilities against that.
  startLiveTv: (
    channelId: string,
    client: ClientCapabilityProfile,
    options: {
      audioStreamIndex?: number;
      maxBitrate?: number;
      maxHeight?: number;
      /// Which plugin the channel is from. Two plugins may both have a channel "1".
      providerId?: string;
      /// The session this one takes the place of - the same channel with another track, or
      /// the channel being switched away from. The server closes it and frees its tuner before
      /// tuning, so the two never compete for one. Only the caller's own session is closed.
      replaces?: string;
    } = {},
  ) =>
    request<{ session: LiveTvSession; streamUrl: string }>('/api/livetv/sessions', {
      method: 'POST',
      body: JSON.stringify({ channelId, client, ...options }),
    }),
  stopLiveTv: (sessionId: string, options?: { keepalive?: boolean }) =>
    request<void>(`/api/livetv/sessions/${sessionId}`, { method: 'DELETE', ...options }),
  liveTvTuners: () => request<LiveTvTunerStatus[]>('/api/livetv/tuners'),

  /// Where one watched channel comes from and how it is received right now.
  liveTvSessionDetails: (sessionId: string) =>
    request<LiveTvSessionDetails>(`/api/livetv/sessions/${sessionId}/details`),

  /// Channels being watched right now, one entry per viewer.
  liveTvSessions: () => request<LiveTvSession[]>('/api/livetv/sessions'),

  /// Plugins that can scan for channels, with what each can scan. Asks the devices, so slow.
  liveTvScanners: () => request<LiveTvScanner[]>('/api/livetv/scan/scanners'),
  /// The running or last scan of every provider that has scanned. Cheap, made for polling.
  liveTvScans: () => request<LiveTvScanStatus[]>('/api/livetv/scan'),
  startLiveTvScan: (scan: LiveTvScanStart) =>
    request<LiveTvScanStatus>('/api/livetv/scan', { method: 'POST', body: JSON.stringify(scan) }),
  cancelLiveTvScan: (providerId: string) =>
    request<LiveTvScanStatus | undefined>(`/api/livetv/scan/${encodeURIComponent(providerId)}`, {
      method: 'DELETE',
    }),

  /// Every channel from every plugin, hidden ones included, with what was changed about it.
  liveTvLineup: () => request<LiveTvLineupChannel[]>('/api/livetv/lineup/channels'),
  liveTvProviders: () => request<LiveTvProviderSummary[]>('/api/livetv/lineup/providers'),
  liveTvSources: () => request<LiveTvSourceSummary[]>('/api/livetv/sources'),
  /// The guide channel ids a channel can be matched to.
  liveTvGuideChannels: () => request<string[]>('/api/livetv/lineup/guide-channels'),
  saveLiveTvOverride: (channelOverride: Omit<LiveTvChannelOverride, 'updatedAt'>) =>
    request<LiveTvChannelOverride>('/api/livetv/overrides', {
      method: 'PUT',
      body: JSON.stringify(channelOverride),
    }),

  liveTvBouquets: () => request<LiveTvBouquetSummary[]>('/api/livetv/bouquets'),
  liveTvBouquet: (bouquetId: string) => request<LiveTvBouquet>(`/api/livetv/bouquets/${bouquetId}`),
  setDefaultLiveTvBouquet: (bouquetId: string) =>
    request<LiveTvBouquetSummary>(`/api/livetv/bouquets/${bouquetId}/default`, { method: 'PUT' }),
  clearDefaultLiveTvBouquet: () =>
    request<void>('/api/livetv/bouquets/default', { method: 'DELETE' }),
  createLiveTvBouquet: (name: string) =>
    request<LiveTvBouquetSummary>('/api/livetv/bouquets', { method: 'POST', body: JSON.stringify({ name }) }),
  renameLiveTvBouquet: (bouquetId: string, name: string) =>
    request<LiveTvBouquetSummary>(`/api/livetv/bouquets/${bouquetId}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),
  deleteLiveTvBouquet: (bouquetId: string) =>
    request<void>(`/api/livetv/bouquets/${bouquetId}`, { method: 'DELETE' }),
  /// Replaces everything in a bouquet with this list, in this order.
  setLiveTvBouquetChannels: (bouquetId: string, channels: LiveTvChannelRef[]) =>
    request<LiveTvBouquet>(`/api/livetv/bouquets/${bouquetId}/channels`, {
      method: 'PUT',
      body: JSON.stringify({ channels }),
    }),
  reorderLiveTvBouquets: (bouquetIds: string[]) =>
    request<LiveTvBouquetSummary[]>('/api/livetv/bouquets/order', {
      method: 'PUT',
      body: JSON.stringify({ bouquetIds }),
    }),

  discoveryProviders: () => request<DiscoveryProvider[]>('/api/discovery/providers'),

  /// What this server offers beyond its shelf, and how it offers it.
  discoveryState: () => request<DiscoveryState>('/api/discovery/state'),

  /// One page of the catalogue, however it was narrowed.
  catalogue: (options: {
    row?: CatalogueRow;
    kind?: DiscoveryKind;
    page?: number;
    query?: string;
    genreId?: number;
  } = {}) => {
    const search = new URLSearchParams({
      row: options.row ?? 'Trending',
      kind: options.kind ?? 'Any',
      page: String(options.page ?? 1),
    });

    if (options.query) {
      search.set('query', options.query);
    }

    if (options.genreId !== undefined) {
      search.set('genreId', String(options.genreId));
    }

    return request<CataloguePage>(`/api/discovery/catalogue?${search.toString()}`);
  },

  /// The genres the catalogue can be narrowed to, in its own naming.
  catalogueGenres: (kind: DiscoveryKind = 'Any') =>
    request<CatalogueGenre[]>(`/api/discovery/genres?kind=${kind}`),

  /// Searches every configured provider at once. The answer is grouped by title, with all the
  /// versions of each under it - which one to watch is a decision for the person, not the server.
  discoverySearch: (query: string, kind: DiscoveryKind = 'Any') =>
    request<DiscoveredTitle[]>(
      `/api/discovery/search?query=${encodeURIComponent(query)}&kind=${kind}`,
    ),
  addedReleases: () => request<DiscoveredRelease[]>('/api/discovery/added'),
  /// Adds one version. Named with its catalogue title where there is one, so the server files it
  /// under that title straight away instead of guessing the title from a file name later.
  addRelease: (providerId: string, releaseId: string, catalogueTitle?: CatalogueTitleRef) =>
    request<DiscoveredRelease>('/api/discovery/added', {
      method: 'POST',
      body: JSON.stringify({ providerId, releaseId, catalogueTitle }),
    }),
  /// A set of versions at once - the episodes of a season. One that cannot be had does not
  /// stop the others; the answer says what came of each.
  addReleases: (providerId: string, releaseIds: string[], catalogueTitle?: CatalogueTitleRef) =>
    request<AddedBatch>('/api/discovery/added/batch', {
      method: 'POST',
      body: JSON.stringify({ providerId, releaseIds, catalogueTitle }),
    }),

  /// Everything the catalogue says about one title, whether or not this server holds it.
  catalogueTitle: (title: CatalogueTitleRef) =>
    request<CatalogueTitleDetails>(titleRoute(title)),

  /// The episodes of one season of a catalogue programme.
  catalogueSeason: (title: CatalogueTitleRef, seasonNumber: number) =>
    request<CatalogueSeasonDetails>(`${titleRoute(title)}/seasons/${seasonNumber}`),

  /// Every version on offer for a title, ranked, with what the server made of each: whether it
  /// carries a language this person wanted, and what its picture is worth. For letting somebody
  /// choose before anything is fetched.
  catalogueTitleVersions: (
    title: CatalogueTitleRef,
    options: {
      seasonNumber?: number;
      episodeNumber?: number;
      preferredAudioLanguages?: string[];
    } = {},
  ) => {
    const search = new URLSearchParams();

    if (options.seasonNumber !== undefined) {
      search.set('seasonNumber', String(options.seasonNumber));
    }

    if (options.episodeNumber !== undefined) {
      search.set('episodeNumber', String(options.episodeNumber));
    }

    for (const language of options.preferredAudioLanguages ?? []) {
      search.append('preferredAudioLanguages', language);
    }

    const query = search.toString();

    return request<CatalogueVersions>(`${titleRoute(title)}/versions${query ? `?${query}` : ''}`);
  },

  /// Makes a catalogue title playable: the version this server holds, or one fetched for it now.
  /// Takes as long as fetching takes - the caller shows that it is under way.
  ///
  /// A release id is the version somebody picked themselves; without one the server picks.
  playOnDemand: (
    title: CatalogueTitleRef,
    episode?: { seasonNumber: number; episodeNumber: number },
    releaseId?: string,
  ) =>
    request<PlayOnDemandResult>(`${titleRoute(title)}/play`, {
      method: 'POST',
      body: JSON.stringify({ ...(episode ?? {}), releaseId }),
    }),
  removeRelease: (providerId: string, releaseId: string) =>
    request<void>(`/api/discovery/added/${providerId}/${encodeURIComponent(releaseId)}`, {
      method: 'DELETE',
    }),

  /// Checks a release right through rather than sampling it. Slow on purpose: this is what
  /// somebody asks for after a film stopped on them.
  repairRelease: (providerId: string, releaseId: string) =>
    request<DiscoveryRepair>(
      `/api/discovery/added/${providerId}/${encodeURIComponent(releaseId)}/repair`,
      { method: 'POST' },
    ),
};
